import Request from "./request";
import ConsentConfig from "../../config/consent-config";

const CONSENTSTORAGEKEY = "userConsentData";
// 30 mins
const CONSENTDATAEXP = 30 * 60 * 1000;

class Consent {
    constructor() {
        this.request = new Request(ConsentConfig);
        this.endpoint = "consent"
    }

    async getClientRegion() {
        const promise = new Promise((resolve, reject) => {
            try {
                // Testing
                //return resolve({ region: "GB", type: "TCF" });
                //return resolve({ region: "US-CA", type: "CPRA" });
                //return resolve({ region: "US-VA", type: "VCDPA" });
                //return resolve({ region: "PH", type: "" });
                // Docs: https://country.is/
                /*
                 * No quotas. Infrastructure rate-limits to 10 requests per second per IP to prevent abuse.
                 */
                const ipRequest = new Request({ protocol: "https", host: "api.country.is" });
                ipRequest.send(
                    "?fields=subdivision",
                    "GET"
                ).then((data) => {
                    if (!data.hasOwnProperty("error")) {
                        // e.g. PH, US, DE
                        const countryCode = data.country;
                        // e.g. CA, VA, CO
                        const regionCode = data.subdivision;

                        let cmpType = "";

                        // Unites States (CPRA / VCDPA)
                        if (countryCode === "US") {
                            if (regionCode === "CA") {
                                cmpType = "CPRA";
                            } else if (["VA", "CO", "CT", "UT"].includes(regionCode)) {
                                cmpType = "VCDPA";
                            }

                            return resolve({ region: `US-${regionCode}`, type: cmpType });
                        }

                        // EU List
                        if (["GB", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
                            "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"
                        ].includes(countryCode)) {
                            return resolve({ region: countryCode, type: "TCF" });
                        }

                        // Rest of the world
                        return resolve({ region: countryCode, type: "" })
                    }

                    return resolve({ region: "", type: "" })
                })

            } catch (error) {
                return resolve({ region: "", type: "" })
            }
        })

        return promise;
    }

    async fetchConsent(userId, type = "cookie", useApi = true) {
        /*
         * userId = cookie user id
         * type = type of consent
         *      - 'cookie' for general cookies
         *      - '...' for checkout
         *      - '...' for franchise/course registration
         * useApi = whether to trigger an API call
         */

        if (type === undefined) {
            throw new Error("Parameter type is required")
        }

        // Make it URL safe
        const encodedUserId = encodeURIComponent(userId);

        // Store user's consent data to localstorage to avoid multiple API calls

        /*
         * If user consent data is already stored in the localstorage, check expiration.
         * If not expired, return the cached list.
         * Otherwise, make an API call to fetch consent data, then store to localstorage
         */
        const promise = new Promise(async (resolve, reject) => {
            let userConsentData = localStorage.getItem(CONSENTSTORAGEKEY);

            let useCache = false;

            if (userConsentData !== null) {
                try {
                    userConsentData = JSON.parse(userConsentData);

                    if (Date.now() < userConsentData.exp) {
                        useCache = true;
                    }
                } catch (error) { }
            }

            if (useCache === true) {
                // If there is a non-expired cache
                resolve(userConsentData);
            } else {
                // If cache is expired or no cache
                try {
                    let data = userConsentData ?? {};

                    if (useApi) {
                        data = await this.request.send(
                            `${this.endpoint}/${type}/main?userId=${encodedUserId}`,
                            "GET"
                        )
                    }

                    if (data.hasOwnProperty("consent")) {
                        // For future: handle different consent types, different keys
                        const exp = Date.now() + CONSENTDATAEXP;
                        localStorage.setItem(CONSENTSTORAGEKEY, JSON.stringify({ ...data, exp }));
                    }

                    resolve(data);
                } catch (err) {
                    reject(err);
                }
            }
        })

        return promise;
    }

    async writeConsent(userId, consent, method = "PUT", type = "cookie", useApi = true) {
        /*
         * userId = cookie user id
         * consent = consent object
         * method = "POST" (new consent), "PUT" (existing consent)
         * type = type of consent
         *      - 'cookie' for general cookies
         *      - '...' for checkout
         *      - '...' for franchise/course registration
         * useApi = whether to trigger an API call
         */

        if (type === undefined) {
            throw new Error("Parameter type is required")
        }

        const body = {
            userId,
            consent
        }

        const promise = new Promise(async (resolve, reject) => {
            try {
                let data = {};

                if (useApi) {
                    data = await this.request.send(
                        `${this.endpoint}/${type}/main`,
                        method,
                        body
                    )
                } else  {
                    data = { ...body, updatedAt: new Date().toISOString() }
                }

                if (data.hasOwnProperty('consent')) {
                    const exp = Date.now() + CONSENTDATAEXP;
                    localStorage.setItem(CONSENTSTORAGEKEY, JSON.stringify({ ...data, exp }));
                }

                resolve(data);
            } catch (err) {
                reject(err)
            }
        })

        return promise;
    }
}

export default Consent;